import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Observable, of, ReplaySubject } from 'rxjs';

import { CountryModel } from '@data-layer/models/common';
import { CodeRepository } from '@data-layer/repositories';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { USA_COUNTRY_CODE } from '@constants/constants';

@Component({
  selector: 'country-dropdown',
  templateUrl: './country-dropdown.component.html',
  styleUrls: ['./country-dropdown.component.scss'],
})
export class CountryDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public fc: UntypedFormGroup;
  @Input() public fcValue: string;
  @Input() public errors: IErrorForm;
  @Input() public defaultCountry: any = null;
  @Input() public isShowRequired: boolean = false;
  @Input() public isDesktop: boolean = false;
  @Input() public isDisabled: boolean = false;
  @Input() public isClearable: boolean = true;
  @Input() public isFilterRestrictedCountries: boolean = false;
  @Input() public isFilterOnlyUSA: boolean;
  @Input() public isFilterOnlyMarineInsurance: boolean;

  public selectedCountry: any = null;

  public countries: CountryModel[] = [];
  public countriesTypeahead: EventEmitter<string> = new EventEmitter<string>();

  public countriesLoading: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private codeRepository: CodeRepository, private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.setControls();

    this.initLoadCountries();

    this.fc.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val: any) => {
      this.selectedCountry = val || null;
      this.cdr.detectChanges();
    }),
      (this.selectedCountry = this.fc.value || null);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // due to the old code & usage of ngModel we have to set selectedCountry manually
    if (changes?.fcValue && this.isDesktop) {
      this.selectedCountry = changes?.fcValue?.currentValue;
    }

    if (changes?.isDisabled) {
      this.initLoadCountries(false);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onChangeCountry(country: CountryModel): void {
    if (!country) {
      this.fc.reset();
      return;
    }

    this.fc.patchValue(country);
    this.fc.markAsDirty();
    this.fc.markAsTouched();
  }

  private initLoadCountries(emitEvent: boolean = true) {
    if (!this.defaultCountry) {
      this.loadCountries();
    } else {
      this.isDisabled = true;
      this.setDefaultCountry(emitEvent);
    }
  }

  private setDefaultCountry(emitEvent: boolean = true): void {
    this.codeRepository
      .getCountry(this.defaultCountry)
      .pipe(
        tap(() => (this.countriesLoading = true)),
        finalize(() => (this.countriesLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe((e: CountryModel) => {
        this.fc.patchValue(e, { emitEvent });
        this.selectedCountry = this.fc.value || null;
      });
  }

  private setControls(): void {
    if (!this.fc) {
      throw new Error(`You can't use this component!`);
    }
  }

  private getCountry(searchStr: string): Observable<CountryModel[]> {
    return this.codeRepository[this.isFilterOnlyMarineInsurance ? 'searchMarineCountry' : 'searchCountry'](searchStr).pipe(
      map((items: CountryModel[]) => {
        if (this.isFilterRestrictedCountries) {
          return items.filter((item: CountryModel) => !item.hasImporterRestriction);
        } else {
          return items;
        }
      }),
    );
  }

  private loadCountries(): void {
    this.countriesTypeahead
      .pipe(
        distinctUntilChanged(),
        debounceTime(300),
        tap(() => (this.countriesLoading = true)),
        switchMap((term: any) =>
          !term
            ? this.emptyCountry()
            : this.getCountry(term).pipe(
                catchError(() => this.emptyCountry()),
                tap(() => (this.countriesLoading = false)),
              ),
        ),
        catchError(() => this.emptyCountry()),
        takeUntil(this.destroy$),
      )
      .subscribe((countriesRes: CountryModel[]) => {
        this.countries = [
          ...countriesRes.filter((item: CountryModel) => {
            if (this.isFilterOnlyUSA !== undefined) {
              return this.isFilterOnlyUSA ? item.code === USA_COUNTRY_CODE : item.code !== USA_COUNTRY_CODE;
            } else {
              return true;
            }
          }),
        ];
        this.cdr.markForCheck();
      });
  }

  private emptyCountry(): Observable<CountryModel[]> {
    this.countriesLoading = false;
    return of([]);
  }
}
